import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot, hydrateRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import {
  appendTrustedScript,
  installScriptSecurityPolicy,
} from "./lib/scriptSecurity";
import "./index.css";
// ?url suffix: Vite resolves these to their final, hashed asset paths at
// build time instead of inlining/bundling the file -- exactly what a
// <link rel="preload"> href needs, since the path can't be known ahead of
// a build (unlike the npm-package-relative import these files already get
// via index.css's @font-face declarations, see Task 4.3).
import archivoNarrowBoldWoff2Url from "@fontsource/archivo-narrow/files/archivo-narrow-latin-700-normal.woff2?url";
import vollkornRegularWoff2Url from "@fontsource/vollkorn/files/vollkorn-latin-400-normal.woff2?url";

installScriptSecurityPolicy();
installHomepageHeroPreload();
installFontPreloads();

/**
 * Preloads the two font weights used on virtually every page's hero
 * headline (Archivo Narrow 700) and body copy (Vollkorn 400) -- the
 * weights most likely to be on the critical rendering path -- so the
 * browser starts fetching them immediately instead of waiting for the
 * CSS's own @font-face discovery (Task 4.3; these are now self-hosted,
 * see index.css's @import block).
 */
function installFontPreloads() {
  for (const href of [archivoNarrowBoldWoff2Url, vollkornRegularWoff2Url]) {
    if (document.querySelector(`link[rel="preload"][href="${href}"]`)) continue;
    const preload = document.createElement("link");
    preload.rel = "preload";
    preload.as = "font";
    preload.type = "font/woff2";
    preload.crossOrigin = "anonymous";
    preload.href = href;
    document.head.appendChild(preload);
  }
}

function installHomepageHeroPreload() {
  if (window.location.pathname !== "/") return;
  if (document.querySelector('link[data-route-preload="home-hero"]')) return;

  const preload = document.createElement("link");
  preload.rel = "preload";
  preload.as = "image";
  preload.type = "image/webp";
  preload.href = "/optimized-media/IMG_1932_d2bcd1b1-768.webp";
  preload.setAttribute("imagesrcset", [
    "/optimized-media/IMG_1932_d2bcd1b1-480.webp 480w",
    "/optimized-media/IMG_1932_d2bcd1b1-640.webp 640w",
    "/optimized-media/IMG_1932_d2bcd1b1-768.webp 768w",
    "/optimized-media/IMG_1932_d2bcd1b1-1024.webp 1024w",
    "/optimized-media/IMG_1932_d2bcd1b1-1600.webp 1600w",
  ].join(", "));
  preload.setAttribute("imagesizes", "100vw");
  preload.setAttribute("fetchpriority", "high");
  preload.dataset.routePreload = "home-hero";
  document.head.appendChild(preload);
}

const analyticsEndpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
const analyticsWebsiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;

if (analyticsEndpoint && analyticsWebsiteId) {
  window.addEventListener(
    "load",
    () => {
      appendTrustedScript(
        `${analyticsEndpoint.replace(/\/$/, "")}/umami`,
        script => {
          script.defer = true;
          script.dataset.websiteId = analyticsWebsiteId;
        },
      );
    },
    { once: true },
  );
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    if (!(error instanceof TRPCClientError)) console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    if (!(error instanceof TRPCClientError)) console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

const app = (
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);

const rootElement = document.getElementById("root")!;
// Task 6.3 (Phase 6 prerendering): a route with a Task 6.4 prerendered
// snapshot arrives with real content already inside #root -- hydrateRoot
// attaches to it instead of discarding and re-rendering from scratch
// (which would both waste the snapshot and cause a visible flash). Any
// route with no snapshot (dev mode; a route Task 6.4 doesn't cover) has
// an empty #root exactly as before, so this falls back to today's exact
// behavior.
if (rootElement.hasChildNodes()) {
  hydrateRoot(rootElement, app);
} else {
  createRoot(rootElement).render(app);
}
